import { CheckIcon, FireIcon } from '@heroicons/react/20/solid';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import useCart from '../../../hooks/use-cart';
import StarRating from '../../../components/star-rating';
import RiyalSymbol from '../../../assets/svg/RiyalSymbol';

interface ProductInfoProps {
  data: any;
  handleDecrement: () => void;
  handleIncrement: () => void;
  quantity: number;
  productId: string;
  tag?: string;
  tagColor?: string;
}

export default function ProductInfo({
  data, productId, handleDecrement, handleIncrement, quantity, tag, tagColor,
}: ProductInfoProps) {
  const { t } = useTranslation();
  const { addItemToCart } = useCart();

  const handleAddToCart = () => {
    addItemToCart(productId, quantity);
  };

  const discountPercentage = data?.discount_percentage;

  return (
    <>
      <div className='flex items-center gap-x-3'>
        <h1 className='text-2xl font-bold tracking-tight text-black'>{data.name}</h1>
        {tag && tagColor && tagColor !== '#000000' && (
          <span
            className='text-white text-xs font-semibold py-1 px-3 rounded-md'
            style={{ backgroundColor: tagColor }}
          >
            {tag}
          </span>
        )}
      </div>

      <div className='flex flex-col sm:flex-row sm:items-baseline justify-between mt-3'>
        <div>
          <div className='flex items-center gap-2'>
            <p className='relative text-sm font-bold tracking-tight text-red-600 line-through flex items-center' dir='ltr'>
              <RiyalSymbol color='currentColor' width={12} height={12} />
              <span>{data.marketing_price}</span>
              <span className='absolute left-0 right-0 top-1/2 border-t border-gray-500' />
            </p>
            <p className='text-lg font-bold tracking-tight text-secondary flex items-center' dir='ltr'>
              <RiyalSymbol color='currentColor' width={16} height={16} />
              <span>{data.base_price_with_vat}</span>
            </p>

            {discountPercentage && (
              <span className='sm:hidden inline-block bg-red-100 text-red-600 text-xs font-semibold px-3 py-1 rounded-md'>
                {t('shared.savePercentage', {
                  percentage: discountPercentage,
                })}
              </span>
            )}
          </div>

          {discountPercentage && (
            <div className='hidden sm:block mt-1'>
              <span className='inline-block bg-red-100 text-red-600 text-xs font-semibold px-3 py-1 rounded-md'>
                {t('shared.savePercentage', {
                  percentage: discountPercentage,
                })}
              </span>
            </div>
          )}
        </div>

        <div className='sm:ml-4 sm:rtl:mr-4 sm:border-l sm:rtl:border-r sm:rtl:border-l-0 sm:border-gray-300 sm:pl-4 sm:rtl:pr-4 mt-3 sm:mt-0'>
          <h2 className='sr-only'>Reviews</h2>
          <div className='flex items-center'>
            <StarRating rating={data.statistics.reviews_average} count={data.statistics.reviews_count} className='h-5 w-5' />
          </div>
        </div>
        <div className='flex items-center gap-2 mt-3 sm:mt-0'>
          {data.in_stock
            ? (
              <div className='flex items-center'>
                <CheckIcon aria-hidden='true' className='h-5 w-5 flex-shrink-0 text-primary' />
                <p className='ml-2 rtl:mr-2 rtl:ml-0 text-sm text-secondary'>{t('productDetails.availability.inStock')}</p>
              </div>
            )
            : (
              <div className='flex items-center'>
                <XMarkIcon aria-hidden='true' className='h-5 w-5 flex-shrink-0 text-red-500' />
                <p className='ml-2 rtl:mr-2 rtl:ml-0 text-sm text-secondary'>{t('productDetails.availability.outOfStock')}</p>
              </div>
            )}
          <div className='flex items-center'>
            <FireIcon aria-hidden='true' className='h-5 w-5 flex-shrink-0 text-primary' />
            <p className='ml-2 rtl:mr-2 rtl:ml-0 text-sm text-secondary'>{t('productDetails.boughtTimes', { count: data.purchases_count })}</p>
          </div>
        </div>
      </div>
      {data.in_stock
        && (
          <div className='mt-7 flex items-center justify-between gap-4'>
            <div className='flex items-center'>
              <p className='text-secondary'>
                {t('productDetails.productInfo.quantity')}
              </p>
              <button
                type='button'
                onClick={handleDecrement}
                className='px-2 border rounded text-secondary hover:bg-primary hover:text-white ltr:ml-2 rtl:mr-2'
              >
                -
              </button>
              <span className='px-2'>{quantity}</span>
              <button
                type='button'
                onClick={handleIncrement}
                className='px-2 border rounded text-secondary hover:bg-primary hover:text-white'
              >
                +
              </button>
            </div>
            <button
              type='button'
              onClick={handleAddToCart}
              className='flex max-w-xs flex-1 items-center justify-center rounded-md border border-transparent bg-primary px-8 py-3 h-11 text-base font-medium text-white shadow-sm hover:bg-lime-600 disabled:bg-lime-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lime-600 sm:w-full'
            >
              {t('cart.addToCart')}
            </button>
          </div>
        )}
    </>
  );
}
